import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { AlchemySideModal } from 'common/alchemyComponents/AlchemySideModal';
import { color, snap, typeStyle, units } from '@m/alchemy-theme';
import AlchemyPage from 'common/alchemyComponents/AlchemyPage';
import { RoundedImage } from 'qa/AssignmentPage/Styled';
import { useSelectorTyped } from 'common/store/useSelectorTyped';
import { LanguageIcon } from '@m/alchemy-ui/icons/LanguageIcon';
import { Text } from '@m/alchemy-ui/Text';
import { UserIcon } from '@m/alchemy-ui/icons/UserIcon';
import LanguagePicker from './LanguagePicker';
import { useFeatureFlag } from 'common/hooks/useFeatureFlags';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  width: 100%;
  border-radius: 8px;
  border: 1px solid ${color('line')};
  margin-top: ${units(5)};
`;

const Item = styled.div`
  width: 100%;
  height: ${units(6)};
  padding: 0 ${units(1.5)};
  &:last-child:not(:first-child) {
    border-top: 1px solid ${color('line')};
  }
  ${Text} {
    gap: ${units(1.2)};
    ${typeStyle('actionM')};
    color: ${color('typeHeader')};
  }
`;

const LogoutContainer = styled.div`
  width: 100%;
  margin-top: ${units(4)};
  border: 1px solid ${color('line')};
  border-radius: ${units(1)};
  display: flex;
  cursor: pointer;
  a {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    align-self: stretch;
    padding: ${units(2)};
  }
`;

const StyledRoundedImage = styled(RoundedImage)`
  width: ${units(12)};
  height: ${units(12)};
`;

const UserActionsSidePanel = (): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const currentPerson = useSelectorTyped(state => state.currentPerson);
  const localizationEnhacements = useFeatureFlag('localization_enhancements');

  const logoutUrl = currentPerson.isAdmin
    ? '/admins/sign_out'
    : '/employees/sign_out';

  useEffect(() => {
    if (!localizationEnhacements) return;
    window.openUserSettings = () => setIsOpen(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!localizationEnhacements) return null;

  return (
    <AlchemyPage>
      <AlchemySideModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        width={snap(460)}
        bodyContent={
          <div className="flex align-center flex-column">
            <div className="flex flex-column align-center justify-center margin-vertical-medium">
              <StyledRoundedImage src={currentPerson.image} />
              <Text typeStyle="dataM" color="typeHeader">
                {currentPerson.name}
              </Text>
              <Text typeStyle="actionM" color="typeMeta">
                {currentPerson.isManager ? 'Team Leader' : 'Team Member'}
              </Text>
            </div>
            <Container>
              { window.Localize != undefined && (
                <Item className="flex align-center">
                  <div className="flex justify-space-between width-100">
                    <Text
                      className="flex justify-start align-center"
                      color="typeMeta"
                    >
                      <LanguageIcon
                        aria-hidden
                        color="contrast"
                        palette="illustrativeLightBlue"
                      />
                      Language:
                    </Text>
                    <LanguagePicker />
                  </div>
                </Item>
              )}
              <Item className="flex align-center">
                <a
                  href={window.selfEditPath}
                  className="width-100 height-100 flex align-center"
                >
                  <Text as="span" className="flex justify-start align-center">
                    <UserIcon
                      aria-hidden
                      color="contrast"
                      palette="illustrativeLightBlue"
                    />
                    Edit My Profile
                  </Text>
                </a>
              </Item>
            </Container>
            <LogoutContainer>
              <a href={logoutUrl}>
                <Text
                  as="span"
                  typeStyle="actionM"
                  color="base"
                  palette="interactiveDestructive"
                >
                  Logout
                </Text>
              </a>
            </LogoutContainer>
          </div>
        }
      />
    </AlchemyPage>
  );
};

export default UserActionsSidePanel;
